import React, { createContext, useContext, useRef } from 'react';
import { createStore, StoreApi } from 'zustand/vanilla';
import { useStore } from 'zustand';

export type BaseFilter = {
  page: number;
};

type FilterStore<T extends BaseFilter> = {
  filter: T;
  setFilter: (partial: Partial<T>) => void;
  appliedCount: number;
  reset: () => void;
};

export function createFilterStore<T extends { page: number }>(defaults: T) {
  type StoreType = StoreApi<FilterStore<T>>;

  const StoreContext = createContext<StoreType | null>(null);

  const Provider: React.FC<{ children: React.ReactNode; initialState?: T }> = ({
    children,
    initialState,
  }) => {
    const storeRef = useRef<StoreType>(null);

    function computeAppliedCount(filters: T) {
      return (Object.entries(filters) as [keyof T, T[keyof T]][]).reduce((count, [key, value]) => {
        if (key === 'page' || !Object.keys(defaults).includes(key as string)) {
          return count;
        }

        const defaultValue = defaults[key];

        if (Array.isArray(value) && JSON.stringify(defaultValue) === JSON.stringify(value)) {
          return count;
        }

        if (value !== null && value !== undefined && value !== defaultValue) {
          return count + 1;
        }

        return count;
      }, 0);
    }

    if (!storeRef.current) {
      storeRef.current = createStore<FilterStore<T>>((set) => ({
        filter: initialState ?? defaults,
        appliedCount: computeAppliedCount(initialState ?? defaults),
        setFilter: (partial) =>
          set((state) => ({
            filter: { ...state.filter, ...partial, page: partial.page ?? 1 },
            appliedCount: computeAppliedCount({ ...state.filter, ...partial }),
          })),
        reset: () =>
          set(() => ({ filter: { ...defaults }, appliedCount: computeAppliedCount(defaults) })),
      }));
    }

    return <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>;
  };

  const useFilterStore = <U,>(selector: (s: FilterStore<T>) => U): U => {
    const store = useContext(StoreContext);
    if (!store) throw new Error('Missing FilterProvider');
    return useStore(store, selector);
  };

  return [Provider, useFilterStore] as const;
}
